import {
  clean,
  plannerPatientMetadataText,
  plannerPatientsShareIdentity,
} from './plannerModels';
import {isSameLocalDay, plannerEventDate} from './plannerDateTime';
import {patientNameFromRecord} from '../../utils/patientNames';

const APPOINTMENT_SOURCES = new Set(['appointment', 'scheduledBot']);

function isAppointmentLikePlannerEvent(event: any) {
  if (!event) return false;
  if (event.stage === 'patient') return false;
  if (event.stage === 'completed' || event.stage === 'active') return false;
  if (APPOINTMENT_SOURCES.has(event.source)) return true;
  if (event.stage !== 'upcoming') return false;
  return (
    Boolean(event.appointmentSummary) ||
    /\b(appointment|scheduled)\b/i.test(clean(event.statusLabel))
  );
}

export function plannerPatientMatchesEvent(patient: any, event: any) {
  return plannerPatientsShareIdentity(patient, event?.patient);
}

export function findScheduledEncounterEvent({
  selectedEvent = null,
  selectedPatient = null,
  sections = [],
  now = new Date(),
}: Record<string, any> = {}) {
  const selectedIsMatch =
    selectedEvent &&
    isAppointmentLikePlannerEvent(selectedEvent) &&
    (!selectedPatient ||
      plannerPatientMatchesEvent(selectedPatient, selectedEvent)) &&
    isSameLocalDay(selectedEvent.sortDate, now);

  if (selectedIsMatch) return selectedEvent;
  if (!selectedPatient) return null;

  const candidates = sections
    .flatMap((section: any) => section.events || [])
    .filter((event: any) => event?.id !== selectedEvent?.id)
    .filter(isAppointmentLikePlannerEvent)
    .filter((event: any) => plannerPatientMatchesEvent(selectedPatient, event))
    .filter((event: any) => isSameLocalDay(event.sortDate, now));

  if (candidates.length === 0) return null;

  const withDates = candidates.map((event: any, index: any) => ({
    event,
    index,
    date: plannerEventDate(event, now),
  }));
  const future = withDates
    .filter(
      ({date}: Record<string, any>) => date && date.getTime() >= now.getTime(),
    )
    .sort(
      (left: any, right: any) =>
        left.date.getTime() - right.date.getTime() || left.index - right.index,
    );
  if (future.length > 0) return future[0].event;

  return (
    withDates.sort((left: any, right: any) => {
      const leftTime = left.date?.getTime?.() ?? Number.NEGATIVE_INFINITY;
      const rightTime = right.date?.getTime?.() ?? Number.NEGATIVE_INFINITY;
      return rightTime - leftTime || left.index - right.index;
    })[0]?.event || null
  );
}

export function buildPlannerPatientHeader(event: any) {
  if (!event) {
    return {
      name: '',
      contextText: '',
    };
  }

  const contextText = plannerPatientMetadataText(event.patient);
  const patientName = event.patient
    ? patientNameFromRecord(event.patient)
    : null;

  return {
    name:
      patientName?.displayName ||
      patientName?.fullName ||
      patientName?.patientName ||
      clean(event.patientName) ||
      'Unknown patient',
    contextText,
  };
}
